import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  createTask,
  getTask,
  updateTask,
  deleteTask,
  reassignTask,
  completeTask,
  reopenTask,
  getTasksFeed,
  getTaskActivities,
  markRecordFollowUpComplete,
  reopenRecordFollowUp,
  type CreateTaskRequest,
  type UpdateTaskRequest,
  type ReassignTaskRequest,
  type CompleteTaskRequest,
} from '@/services/api/task.service'
// ─── Tasks feed hook ──────────────────────────────────────────────────────────

export function useTasksFeed(
  section: 'remaining' | 'completed' = 'remaining',
  dateRange: { from?: string; to?: string } = {}
) {
  return useQuery({
    queryKey: ['tasksFeed', section, dateRange.from ?? null, dateRange.to ?? null],
    queryFn: () => getTasksFeed(section, dateRange),
    staleTime: 30 * 1000,
  })
}

// ─── Single task hook ──────────────────────────────────────────────────────────
export function useTask(taskId: string) {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => getTask(taskId),
    enabled: !!taskId,
    staleTime: 30 * 1000,
  })
}

// ─── Task mutations ────────────────────────────────────────────────────────────
export function useCreateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTaskRequest) => createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: UpdateTaskRequest }) =>
      updateTask(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (taskId: string) => deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
    },
  })
}

export function useReassignTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: ReassignTaskRequest }) =>
      reassignTask(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}

export function useCompleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: CompleteTaskRequest }) =>
      completeTask(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}

export function useReopenTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: CompleteTaskRequest }) =>
      reopenTask(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
}

// ─── Record follow-up mutations ───────────────────────────────────────────────
export function useMarkRecordFollowUpComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ recordId, data }: { recordId: string; data: { recordActivitySessionId: string | null; note: string | null; nextActivityAt?: string | null; nextActivityInstructions?: string | null; tagId?: string | null; followUpTagId?: string | null } }) =>
      markRecordFollowUpComplete(recordId, data),
    onSuccess: (_, { recordId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })
}

export function useReopenRecordFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ recordId, data }: { recordId: string; data: { recordActivitySessionId: string | null; note: string | null; nextActivityAt?: string | null; nextActivityInstructions?: string | null; tagId?: string | null; followUpTagId?: string | null } }) =>
      reopenRecordFollowUp(recordId, data),
    onSuccess: (_, { recordId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasksFeed'] })
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })
}

// ─── Task activities hook ─────────────────────────────────────────────────────
export function useTaskActivities(taskId: string) {
  return useQuery({
    queryKey: ['taskActivities', taskId],
    queryFn: () => getTaskActivities(taskId),
    enabled: !!taskId,
    staleTime: 30 * 1000,
  })
}
